import LegalLayout, { Section } from "@/components/LegalLayout";
import { useSeo } from "@/lib/useSeo";

export default function Terms() {
  useSeo({
    title: "Terms of Service | Social Media Combo",
    description:
      "The terms governing your use of socialmediacombo.com and the purchase of our digital products, including licensing, refunds, and income disclaimers.",
    path: "/terms",
  });
  return (
    <LegalLayout title="Terms of Service" updated="June 2026">
      <p className="text-zinc-400 leading-relaxed">
        These Terms of Service ("Terms") govern your access to and use of{" "}
        <strong>socialmediacombo.com</strong> (the "Site") and the purchase of any digital
        products offered by Social Media Combo ("we", "us", or "our"). By using the Site or
        buying our products, you agree to these Terms.
      </p>

      <Section heading="1. Digital Products">
        <p>
          We sell digital products, including ebooks, guides, templates, and content bundles.
          All products are delivered electronically. No physical goods will be shipped.
        </p>
      </Section>

      <Section heading="2. License & Permitted Use">
        <p>
          Unless a product is explicitly sold with Master Resell Rights (MRR) or a stated
          commercial/resale license, your purchase grants you a personal, non-transferable
          license to use the materials for your own use. Where a product includes resell or
          commercial rights, those specific rights are described within the product itself and
          govern your permitted use.
        </p>
        <p>
          You may not copy, redistribute, or resell any product that does not include explicit
          resale rights.
        </p>
      </Section>

      <Section heading="3. Payments">
        <p>
          All payments are processed securely through Stripe. Prices are listed in U.S. dollars
          unless otherwise stated. By purchasing, you confirm that the payment information you
          provide is accurate and that you are authorized to use the payment method.
        </p>
      </Section>

      <Section heading="4. Refund & Money-Back Guarantee">
        <p>
          We offer a 30-day money-back guarantee on our bundle purchases. If you are not
          satisfied, contact us within 30 days of purchase at{" "}
          <a href="mailto:ebooks@socialmediacombo.com" className="text-[#D4AF37] underline">
            ebooks@socialmediacombo.com
          </a>{" "}
          to request a refund. We reserve the right to decline refund requests that show evidence
          of abuse of this policy.
        </p>
      </Section>

      <Section heading="5. Earnings & Income Disclaimer">
        <p>
          Any income figures, results, or examples shown on this Site (including references to
          potential monthly earnings) are illustrative and are <strong>not</strong> a guarantee
          or promise of your results. They represent exceptional outcomes and should not be
          interpreted as typical.
        </p>
        <p>
          Your results depend on many factors, including your effort, skill, market conditions,
          and how you apply the material. We make no guarantee that you will earn any specific
          amount of money — or any money at all. You are solely responsible for your own actions
          and results.
        </p>
      </Section>

      <Section heading="6. Intellectual Property">
        <p>
          All content on the Site and within our products — including text, graphics, logos, and
          design — is owned by or licensed to Social Media Combo and is protected by intellectual
          property laws. You may not use our branding without prior written permission.
        </p>
      </Section>

      <Section heading="7. Acceptable Use">
        <p>You agree not to use the Site or products to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Violate any applicable law or regulation.</li>
          <li>Infringe the rights of others.</li>
          <li>Distribute malware or engage in fraudulent activity.</li>
          <li>Resell or redistribute products without authorized rights.</li>
        </ul>
      </Section>

      <Section heading="8. Disclaimer of Warranties">
        <p>
          The Site and products are provided "as is" and "as available" without warranties of any
          kind, whether express or implied. We do not warrant that the Site will be uninterrupted,
          error-free, or that the products will meet your expectations.
        </p>
      </Section>

      <Section heading="9. Limitation of Liability">
        <p>
          To the fullest extent permitted by law, Social Media Combo shall not be liable for any
          indirect, incidental, special, or consequential damages arising from your use of the
          Site or products. Our total liability shall not exceed the amount you paid for the
          product in question.
        </p>
      </Section>

      <Section heading="10. Third-Party Links">
        <p>
          The Site may contain links to third-party websites or services (such as payment or email
          providers). We are not responsible for the content or practices of those third parties.
        </p>
      </Section>

      <Section heading="11. Changes to These Terms">
        <p>
          We may update these Terms at any time. Continued use of the Site after changes are posted
          constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section heading="12. Contact Us">
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:ebooks@socialmediacombo.com" className="text-[#D4AF37] underline">
            ebooks@socialmediacombo.com
          </a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
